import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./List.css";
import { GetSeedDistributionData } from "./Common";

const List = () => {
  const navigate = useNavigate();
  const { orgCode } = useParams();

  const [fetchedUserId, setFetchedUserId] = useState("");
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [showImagePopup, setShowImagePopup] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalRecords: 0,
    totalPages: 1,
  });

  const varietyOptions = [
    { id: 2001, name: "PADMA 141" },
    { id: 2002, name: "PADMA VASUDHA 1318 GOLD" },
    { id: 2003, name: "PARTECH 300" },
    { id: 2004, name: "ASHA NON BT" },
    { id: 2005, name: "PARSHV-909" },
    { id: 2006, name: "PARSHV-101" },
  ];
  const cropOptions = [{ id: 2000, name: "Cotton" }];

  const getVarietyName = (id) =>
    varietyOptions.find((v) => v.id === id)?.name || "N/A";
  const getCropName = (id) =>
    cropOptions.find((c) => c.id === id)?.name || "N/A";

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleNavigate = () => {
    if (orgCode && fetchedUserId) {
      navigate(`/data-collection/${orgCode}/${fetchedUserId}`);
    } else {
      navigate("/");
    }
  };

  const handleViewImage = (url) => {
    if (!url) {
      alert("No image available.");
      return;
    }
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error("Image not found");
        return res.blob();
      })
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        setPreviewImageUrl(objectUrl);
        setShowImagePopup(true);
      })
      .catch((err) => {
        console.error("Failed to load image:", err);
        alert("Failed to load image.");
      });
  };

  const fetchData = async () => {
    const { currentPage, pageSize } = pagination;
    const payload = {
      Org_Code: orgCode,
      page: currentPage,
      limit: pageSize,
      sortBy: "createdAt",
      sortOrder: "desc",
      search,
    };

    try {
      const response = await GetSeedDistributionData(payload);
      const responseData = response?.data?.data || [];
      const pageInfo = response?.data?.pagination;
      setData(responseData);
      setFetchedUserId(responseData[0]?.User_ID || "");

      if (pageInfo) {
        setPagination((prev) => ({
          ...prev,
          totalRecords: Number(pageInfo.totalRecords) || responseData.length,
          totalPages: Number(pageInfo.totalPages) || 1,
        }));
      } else {
        const totalRecords = responseData.length;
        setPagination((prev) => ({
          ...prev,
          totalRecords,
          totalPages: Math.ceil(totalRecords / pageSize) || 1,
        }));
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setData([]);
    }
  };

  useEffect(() => {
    fetchData();
  }, [pagination.currentPage, pagination.pageSize, search, orgCode]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value, 10) || 10;
    setPagination((prev) => ({ ...prev, pageSize: newSize, currentPage: 1 }));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, currentPage: newPage }));
    }
  };

  const { currentPage, pageSize, totalRecords, totalPages } = pagination;

  return (
    <div className="page">
      <div className="inner-page-wrapper">
        <section className="division wide-30">
          <div className="container">
            <div
              className="header"
              style={{
                position: "relative",
                textAlign: "center",
                marginBottom: "20px",
              }}
            >
              <h2 style={{ margin: 0 }}>Seed Distribution List</h2>
              <button onClick={handleNavigate} className="back-button">
                ← Back
              </button>
            </div>

            <div className="card">
              <div className="card-body">
                <div className="controls">
                  <div className="show-select">
                    <label>Show</label>
                    <select value={pageSize} onChange={handlePageSizeChange}>
                      {[10, 25, 50, 100].map((num) => (
                        <option key={num} value={num}>
                          {num}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="search-box">
                    <input
                      type="search"
                      placeholder="Search..."
                      value={search}
                      onChange={handleSearchChange}
                    />
                  </div>
                </div>

                <div className="table-wrapper">
                  <table className="custom-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Tracenet Code</th>
                        <th>Name</th>
                        <th>Crop Name</th>
                        <th>Variety</th>
                        <th>Packets</th>
                        <th>Lot No</th>
                        <th>Thumb</th>
                        <th>Signature</th>
                        <th>Image</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.length === 0 ? (
                        <tr>
                          <td colSpan="11" className="text-center">
                            No records found
                          </td>
                        </tr>
                      ) : (
                        data.map((item, index) => {
                          const formattedDate = new Date(
                            item.Date_of_Distributation
                          ).toLocaleDateString("en-GB");
                          return (
                            <tr key={item.Seed_Distribution_ID}>
                              <td>
                                {(currentPage - 1) * pageSize + index + 1}
                              </td>
                              <td>{item.user?.Mobile_Number || "N/A"}</td>
                              <td>{item.user?.User_Name || "N/A"}</td>
                              <td>{getCropName(item.Crop_ID)}</td>
                              <td>{getVarietyName(item.Variety_ID)}</td>
                              <td>{item.No_Of_Packet || "N/A"}</td>
                              <td>{item.LotNo || "N/A"}</td>
                              <td>
                                {item.Signature_Thumb ? (
                                  <button
                                    onClick={() =>
                                      handleViewImage(item.Signature_Thumb)
                                    }
                                    className="text-blue-600 hover:underline text-sm font-semibold"
                                  >
                                    View Fingerprint
                                  </button>
                                ) : (
                                  "N/A"
                                )}
                              </td>
                              <td>
                                {item.Signature_Pad ? (
                                  <button
                                    onClick={() =>
                                      handleViewImage(item.Signature_Pad)
                                    }
                                    className="text-blue-600 hover:underline text-sm font-semibold"
                                  >
                                    View Signature
                                  </button>
                                ) : (
                                  "N/A"
                                )}
                              </td>
                              <td>
                                {item.Farmer_Image ? (
                                  <button
                                    onClick={() =>
                                      handleViewImage(item.Farmer_Image)
                                    }
                                    className="text-blue-600 hover:underline text-sm font-semibold"
                                  >
                                    View Image
                                  </button>
                                ) : (
                                  "N/A"
                                )}
                              </td>
                              <td>{formattedDate}</td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="pagination-container">
                  <div>
                    Showing{" "}
                    {totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1}{" "}
                    to {Math.min(currentPage * pageSize, totalRecords)} of{" "}
                    {totalRecords} entries
                  </div>
                  <div className="pagination-buttons">
                    <button
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                    >
                      Previous
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (page) => (
                        <button
                          key={page}
                          className={page === currentPage ? "active" : ""}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      )
                    )}
                    <button
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>

                {showImagePopup && previewImageUrl && (
                  <div
                    style={{
                      position: "fixed",
                      top: 0,
                      left: 0,
                      width: "100vw",
                      height: "100vh",
                      backgroundColor: "rgba(0, 0, 0, 0.6)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 9999,
                    }}
                    onClick={() => {
                      setShowImagePopup(false);
                      setPreviewImageUrl(null);
                    }}
                  >
                    <img
                      src={previewImageUrl}
                      alt="Preview"
                      style={{
                        maxWidth: "90%",
                        maxHeight: "90%",
                        background: "#fff",
                        padding: "10px",
                        borderRadius: "8px",
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default List;
